import { replaceEqualDeep } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
/** Subscribe only after explicit field access, keeping Items-only owners isolated. */
export function useTrackedQueryValue(client, queryHash, selectValue, includeEntityUpdates) {
  const selectorRef = useRef(selectValue);
  useEffect(() => {
    selectorRef.current = selectValue;
  });
  const selection = useMemo(() => {
    let tracked = false;
    let snapshot = null;
    const getSnapshot = () => {
      if (tracked) snapshot = replaceEqualDeep(snapshot, selectorRef.current());
      return snapshot;
    };
    return {
      getSnapshot,
      track: () => {
        tracked = true;
        getSnapshot();
      },
      subscribe: notify => {
        let pending = false;
        let active = true;
        const release = client.getQueryCache().subscribe(event => {
          if (!tracked || pending || event.type !== 'removed' && (event.type !== 'updated' || event.action.type !== 'success')) return;
          if (!includeEntityUpdates && event.query.queryHash !== queryHash) return;
          pending = true;
          // A normalized snapshot can write hundreds of entity keys in one batch.
          queueMicrotask(() => {
            pending = false;
            if (!active) return;
            const before = snapshot;
            if (getSnapshot() !== before) notify();
          });
        });
        return () => {
          active = false;
          release();
        };
      }
    };
  }, [client, queryHash, includeEntityUpdates]);
  useSyncExternalStore(selection.subscribe, selection.getSnapshot, selection.getSnapshot);
  return selection.track;
}